"use client";

import type React from "react";
import { createContext, useState, useContext, useEffect } from "react";

type Theme = "light" | "dark";

type ThemeContextType = {
  theme: Theme;
  toggleTheme: () => void;
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [theme, setTheme] = useState<Theme>("light");
  const [isInitialized, setIsInitialized] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  useEffect(() => {
    if (!isMounted) return;
    
    // This code will only run on the client side
    const savedTheme = localStorage.getItem("theme") as Theme | null;
    const initialTheme = savedTheme || "light"; // Default to light theme

    setTheme(initialTheme);
    setIsInitialized(true);
  }, [isMounted]);

  useEffect(() => {
    if (!isInitialized || !isMounted) return;
    
    // Use requestAnimationFrame to ensure DOM is ready
    const updateTheme = () => {
      try {
        localStorage.setItem("theme", theme);
        const htmlElement = document.documentElement;
        
        if (htmlElement) {
          if (theme === "dark") {
            htmlElement.classList.add("dark");
          } else {
            htmlElement.classList.remove("dark");
          }
        }
      } catch (error) {
        console.warn("Theme update failed:", error);
      }
    };
    
    requestAnimationFrame(updateTheme);
  }, [theme, isInitialized, isMounted]);

  const toggleTheme = () => {
    if (!isMounted) return;
    setTheme((prevTheme) => (prevTheme === "light" ? "dark" : "light"));
  };

  // Don't render children until mounted to avoid hydration issues
  if (!isMounted) {
    return (
      <ThemeContext.Provider value={{ theme: "light", toggleTheme: () => {} }}>
        {children}
      </ThemeContext.Provider>
    );
  }

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
